/**
 * Générateur — Automatisme : entretien des tables de multiplication.
 * Attendu ID : 1093 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 634 :
 * « Entretien des tables de multiplication. »).
 *
 * ── WRAPPER de gen_1047 (5e, « connaître et utiliser les tables ») ──────────
 * Le mot du programme est « entretien » : rien de nouveau à mémoriser, la
 * borne des tables (facteurs 2 à 9) n'est jamais relevée au collège. C'est le
 * même automatisme que 1047 en 5e et 1058 en 5e (rubrique Puissances) ; ce
 * gen DÉLÈGUE intégralement, comme eux : zéro duplication, une seule
 * maintenance. Le payload est re-badgé (niveau 4eme, attendu 1093) ; cartes,
 * formats, generator_keys, invariants, distracteurs et couple sacrifié (7 × 8)
 * sont ceux de 1047. Coup de pouce et solveurs réutilisent l'entrée 1047.
 *
 * Ce que 1093 ne contient PAS, et pourquoi : le périmètre observé de
 * MathsMentales (page 4e, activité 5NC5 « Multiplication de relatifs »,
 * produits de deux ou trois entiers entre -10 et 10) range sous cet automatisme
 * des produits SIGNÉS. Or « multiplier deux nombres relatifs » est un OBJECTIF
 * d'apprentissage de 4e (l. 640-641), pas un automatisme : la règle des signes
 * s'apprend en 4e, elle n'est pas encore automatisable. Les tables, si.
 * (Périmètre observé : MathsMentales, S. Cogez, Apache 2.0, relevé 2026-09-12.)
 */
import { generate as genererTables, CATEGORIES as CAT_TABLES } from '../../5eme/nombres-rationnels/gen_1047';
import { retirerCodesErreur } from '../../../utils';

const ATTENDU_ID = 1093;

export const CATEGORIES = CAT_TABLES;

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Les cinq familles de 1047 ne font qu'un code commun : les tables mal sues, le fait de table voisin (6 × 9 = 56). */
const TABLE = 'fait_de_table_voisin';
const CODES_TABLE = ['voisin_meme_table', 'voisin_autre_table', 'couple_voisin', 'facteur_voisin', 'saut_de_deux'];
const dansTable = (n) => n >= 2 && n <= 9;

/** Les résultats voisins de a × b, comme les distracteurs de 1047 : un facteur à un cran, ou le couple voisin. */
const voisinsProduit = (a, b) => [...new Set([[a, b - 1], [a, b + 1], [a - 1, b], [a + 1, b], [a - 1, b + 1], [a + 1, b - 1]]
    .filter(([x, y]) => dansTable(x) && dansTable(y)).map(([x, y]) => x * y))].filter((p) => p !== a * b);

/** Les facteurs voisins de k : à un ou deux crans, dans la table. */
const voisinsFacteur = (k) => [k - 1, k + 1, k - 2, k + 2].filter(dansTable);

/** Une ligne du Vrai/Faux, « 7 × 8 = 54 » ou « 54 = 7 × 8 » : { a, b, p }, null sinon. */
const lireEgalite = (ligne) => {
    const m = ligne.match(/^(\d+) × (\d+) = (\d+)$/);
    if (m) return { a: Number(m[1]), b: Number(m[2]), p: Number(m[3]) };
    const n = ligne.match(/^(\d+) = (\d+) × (\d+)$/);
    return n ? { a: Number(n[2]), b: Number(n[3]), p: Number(n[1]) } : null;
};

/** Les erreurs du format tiré, recalculées depuis le payload de 1047 (lu, jamais modifié). */
const erreursTables = (payload) => {
    const params = payload._meta?.params || {};
    const c = payload.contenu || {};
    switch (payload._meta?.generatorKey) {
        case 'flash_texte_trou_saisie':
            return (params.items || []).flatMap((it, trou) => (it.forme === 'produit' ? voisinsProduit(it.a, it.b) : voisinsFacteur(it.trouSurA ? it.a : it.b))
                .map((v) => ({ code: TABLE, trou, valeur: String(v) })));
        case 'sens_qcm_unique': {
            const voisins = params.forme === 'produit' ? voisinsProduit(params.a, params.b) : voisinsFacteur(params.b);
            return (c.choix || []).filter((ch) => voisins.includes(Number(ch))).map((ch) => ({ code: TABLE, valeur: ch }));
        }
        case 'sens_tableau_vf': {
            const table = (payload.elements_additionnels?.elements || []).find((e) => e.type === 'table')?.config;
            return (table?.lignes || []).filter((ligne, i) => {
                const e = table.bonnesReponses[i] === 1 && lireEgalite(ligne);
                return e && voisinsProduit(e.a, e.b).includes(e.p);
            }).map((ligne) => ({ code: TABLE, ligne }));
        }
        default:
            return [];
    }
};

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    sens_tableau_vf: ['fait_de_table_voisin'],
};

export const generate = (typeExercice) => {
    const payload = genererTables(typeExercice);
    payload.niveau = '4eme';
    payload.attendu = ATTENDU_ID;
    // Relier : les codes des cibles leurres deviennent le code commun ; ailleurs, contenu.erreurs
    if (Array.isArray(payload.contenu?.cibles_leurres)) {
        payload.contenu = { ...payload.contenu, cibles_leurres: payload.contenu.cibles_leurres.map((l) => (CODES_TABLE.includes(l.code) ? { ...l, code: TABLE } : l)) };
    } else payload.contenu = { ...payload.contenu, erreurs: erreursTables(payload) };
    retirerCodesErreur(payload, CODES_RETIRES[payload._meta?.generatorKey]);
    return payload;
};
